/**
 * Thin, typed wrappers around `window.fbq` for the Meta pixel events theaWeb
 * fires. The pixel snippet itself is loaded by `public/index.html`; these
 * helpers exist to:
 *
 *   - Centralize the "is fbq actually loaded yet?" + bot-detection guards so
 *     callers don't sprinkle the same `typeof` check at every fire site.
 *   - Give each event a typed param interface so we don't quietly drift the
 *     vocabulary across surfaces.
 *   - Defer every fire to `requestIdleCallback` so analytics never blocks
 *     paint, INP, or LCP (analytics handoff §16 perf budget rule #1).
 *   - Keep params PII-free (no name, email, uid, recipientId). Funnel
 *     anonymized params only — Kate may tweak the shape before paid spend.
 *
 * Pixel ID 2511117595971258 (set in public/index.html).
 *
 * ## Two gates live here, and they are NOT the same gate
 *
 * 1. **Consent (every visitor).** The pixel script is never even loaded until
 *    the visitor accepts (see `consentTags.ts` — fbq has no consent API, so
 *    "don't load it" is the only real off switch). `canFire()` additionally
 *    refuses to call `fbq` so the gate is explicit rather than an accident of
 *    `window.fbq` being undefined.
 *
 * 2. **Child recipients.** Nothing derived from a child recipient reaches
 *    `fbq`, consented or not — an independent gate that does not lean on the
 *    consent one. It withholds child *attributes*, not necessarily the whole
 *    fire: `QuizResultsViewed` still fires with an emptied param bag so the
 *    completion is still counted. See `consent/childAudience.ts` for the full
 *    rationale.
 *
 * ## Server-side CAPI is deliberately NOT consent-gated
 *
 * `sendCapiEvent` keeps firing with or without banner consent. That is an
 * explicit product decision, not an oversight — do not "fix" it. The
 * consequence for this file is structural: the two functions that mirror to
 * CAPI (`metaPageView`, `metaViewContent`) must NOT early-return on the
 * consent check, or they would take CAPI down with the pixel. They branch
 * around the `fbq` call instead.
 */
import { isAdvertisingGranted } from '../consent/consentStore';
import {
  NO_RECIPIENT,
  isChildAgeBucket,
  isChildAudience,
  minimizeChildQuizParams,
  stripChildDerivedFields,
  type RecipientAudience,
} from '../consent/childAudience';
import { isBot } from './botDetect';
import { fireWhenIdle } from './idleCallback';
import { generateEventId, sendCapiEvent } from './metaCapi';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

/**
 * May we call `window.fbq` right now? Loaded, not a bot, and consent permits.
 *
 * Use this for pixel-only events. Events that also mirror to CAPI must branch
 * on it rather than early-return — see the docblock above.
 */
function canFire(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.fbq !== 'undefined' &&
    !isBot() &&
    isAdvertisingGranted()
  );
}

/** The bot/environment guard that applies to BOTH transports. */
function isLiveBrowser(): boolean {
  return typeof window !== 'undefined' && !isBot();
}

/**
 * Standard PageView. Fired by `usePageTracking` on every route change.
 *
 * NOTE: PageView is the attribution gate — it's the event Meta uses to
 * record a Landing Page View, which feeds the algorithm's optimization.
 * For this event we DO NOT defer via `fireWhenIdle`, deliberately:
 *
 * fast-bouncing paid-social users leave within 2-3 seconds of click. The
 * idle window often doesn't open in that timeframe on a busy mobile
 * webview, so a deferred PageView fire never reaches Meta even though
 * the user did render the page. Firing synchronously costs ~0.01ms (one
 * push to the fbq queue, since fbevents.js is still loading async at
 * this point) — zero CWV impact, but recovers the bouncer-attribution
 * Meta otherwise can't see.
 *
 * The CAPI mirror (server-side) uses the same `eventId` for dedup, so
 * Meta counts at most once per fire even if both arrive.
 */
export function metaPageView(): void {
  if (!isLiveBrowser()) return;
  const eventId = generateEventId();
  // NOTE the branch rather than an early return: CAPI must still fire for an
  // un-consented visitor (requirement (a)). PageView carries no recipient
  // data, so there is no child check here.
  if (canFire()) {
    // 4th arg `eventID` is Meta's documented FE→CAPI dedup key.
    window.fbq?.('track', 'PageView', {}, { eventID: eventId });
  }
  sendCapiEvent('PageView', eventId);
}

/** Anonymized funnel params — kept PII-free deliberately. */
export interface QuizSearchSubmittedParams {
  occasion?: string;
  relationship?: string;
  age_range?: string;
  interest_count?: number;
  /** Whether the freeform "tell us more" field was filled in. */
  has_freeform?: boolean;
  /** From recipient.gender. */
  gender?: string;
}

/**
 * Custom event — fired when the quiz submit callable resolves successfully.
 *
 * SUPPRESSED ENTIRELY for child recipients. Stripping `age_range` alone would
 * not be enough: `{relationship: 'Daughter', gender: 'female', occasion:
 * 'Birthday'}` is still a profile of somebody's kid. The event *is* the child's
 * quiz answers, so the whole event is withheld. Pixel-only — no CAPI mirror.
 */
export function metaQuizSearchSubmitted(params: QuizSearchSubmittedParams): void {
  if (isChildAgeBucket(params.age_range)) return;
  fireWhenIdle(() => {
    if (!canFire()) return;
    window.fbq?.('trackCustom', 'QuizSearchSubmitted', params);
  });
}

export interface QuizResultsViewedParams {
  occasion?: string;
  relationship?: string;
  age_range?: string;
  interest_count?: number;
  carousel_count: number;
  product_count: number;
}

/**
 * Custom event — fired once when the results page first lands a COMPLETED
 * session. Pixel-only — no CAPI mirror.
 *
 * DATA-MINIMIZED, NOT SUPPRESSED, for child recipients. Unlike
 * `metaQuizSearchSubmitted` this event is the funnel-completion signal: it is
 * what Meta counts, optimizes toward, and builds the quiz-completer audience
 * from. Dropping it withheld the child's attributes (correct) and the fact
 * that a quiz happened at all (not asked for, and not child data). So the fire
 * survives with its param bag reduced to the child-safe allowlist — which is
 * empty, so in practice an empty object. See `minimizeChildQuizParams`.
 *
 * Same fail-safe as everywhere else: an absent `age_range` minimizes, because
 * an unrecorded age cannot be proven to be an adult's.
 */
export function metaQuizResultsViewed(params: QuizResultsViewedParams): void {
  const isChild = isChildAgeBucket(params.age_range);
  fireWhenIdle(() => {
    if (!canFire()) return;
    window.fbq?.(
      'trackCustom',
      'QuizResultsViewed',
      isChild ? minimizeChildQuizParams(params) : params,
    );
  });
}

export interface ViewContentParams {
  content_name: string;
  content_ids: string[];
  content_category?: string;
  value?: number;
  currency?: string;
}

/** Standard ViewContent. Fired when a user clicks a product card to open the affiliate URL.
 *
 * Mirrored to CAPI (server-side) with shared `eventId` for dedup. ViewContent is
 * Thea's closest analog to a "purchase intent" signal pre-revenue (the click-out
 * goes through Sovrn affiliate); we want Meta to see this event reliably even
 * when iOS ATT or ad blockers stop the FE pixel from attributing it.
 *
 * `audience` says whose board the product came from. It defaults to
 * `NO_RECIPIENT` because most callers (guides, the teacher landing page, the
 * marketing carousels) have no quiz recipient in scope at all. Board surfaces
 * that DO know the recipient must pass one — see `fireProductOpen`.
 *
 * For a child recipient the pixel fire is dropped entirely (the products are
 * that child's quiz results) while CAPI still fires with `content_category`
 * stripped — that field is the backend-generated carousel title and is the one
 * value that can describe the child in prose. See `childAudience.ts`.
 */
export function metaViewContent(
  params: ViewContentParams,
  audience: RecipientAudience = NO_RECIPIENT,
): void {
  if (!isLiveBrowser()) return;

  const isChild = isChildAudience(audience);
  const eventId = generateEventId();

  // Branch, don't early-return: CAPI must survive both the consent gate
  // (requirement (a)) and the child gate (requirement (b) names the pixel).
  if (canFire() && !isChild) {
    fireWhenIdle(() => {
      window.fbq?.('track', 'ViewContent', params, { eventID: eventId });
    });
  }

  const capiParams = isChild ? stripChildDerivedFields(params) : params;
  sendCapiEvent('ViewContent', eventId, { customData: capiParams });
}

export interface PromoClickParams {
  promotion_id: string;
  promotion_name: string;
  creative_name: string;
  location_id: string;
}

/**
 * Custom event — Meta mirror of GA4 `select_promotion`. Fired when a banner
 * CTA is clicked. Useful for Meta audience modeling on quiz-engaged users
 * coming from a specific creative.
 */
export function metaPromoClick(params: PromoClickParams): void {
  fireWhenIdle(() => {
    if (!canFire()) return;
    window.fbq?.('trackCustom', 'PromoClick', params);
  });
}

/**
 * Bucketize a numeric age into the coarse buckets we report on. Keeps the
 * pixel param low-cardinality (helpful for Meta's audience modeling) and
 * matches the old codebase's analytics shape.
 */
export function ageBucket(age: number | undefined): string | undefined {
  if (age == null || Number.isNaN(age)) return undefined;
  if (age < 13) return 'under_13';
  if (age < 20) return 'teen';
  if (age < 30) return '20s';
  if (age < 40) return '30s';
  if (age < 50) return '40s';
  if (age < 60) return '50s';
  if (age < 70) return '60s';
  return '70_plus';
}
